import { Stack } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledChatAgentStep from '../../../styles/StyledChatAgentStep';
import StyledFormItem from '../../../styles/StyledFormItem';
import AutocompleteInput from '../../shared/AutoCompleteInput/AutoCompleteInput';
import ChatConfigPreview from '../../shared/ChatConfigPreview/ChatConfigPreview';
import Input from '../../shared/Input/Input';
import Text from '../../shared/Text/Text';
import ToolTip from '../../shared/ToolTip/ToolTip';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import ResourceAccessFields from '../../shared/ResourceAccessFields/ResourceAccessFields';

interface ChatAgentSettingsStepProps {
  stepLabel?: string;
  label?: string;
  description?: string;
  showAccessFields?: boolean;
  ownerUserId?: string;
}
const ChatAgentSettingsStep: React.FC<ChatAgentSettingsStepProps> = ({
  stepLabel,
  label,
  description,
  showAccessFields = false,
  ownerUserId,
}) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  const formData = watch();
  const startMessage = watch('start_message') as string | undefined;
  const [activeField, setActiveField] = useState<string | undefined>(undefined);

  return (
    <StyledChatAgentStep>
      <Stack direction="column" width="364px">
        <WorkflowStepLabel
          stepLabel={stepLabel}
          label={label ?? t('Chat Agent Settings')}
          description={description}
        />
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Chat Name')}
              <ToolTip
                title={t(
                  'The internal name for this chat agent -- not visible to the users'
                )}
              />
            </Text>
          </Stack>
          <Input
            name="name"
            required={true}
            withError={!!errors.name}
            helperText={!!errors.name ? t('Chat name is required') : ''}
          />
        </StyledFormItem>
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Chat Title')}
              <ToolTip title={t('The display title shown to users')} />
            </Text>
          </Stack>
          <Input
            name="header_text"
            required={true}
            withError={!!errors.header_text}
            helperText={!!errors.header_text ? t('Chat title is required') : ''}
          />
        </StyledFormItem>
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Chat Start Message')}
              <ToolTip
                title={t(
                  'The first message the agent sends when the chat opens'
                )}
              />
            </Text>
          </Stack>
          <Input
            name="start_message"
            onFocus={() => setActiveField('start_message')}
            onBlur={() => setActiveField(undefined)}
          />
        </StyledFormItem>
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Prompt Suggestions')}
              <ToolTip
                title={t(
                  'Suggested prompts to help users start the conversation'
                )}
              />
            </Text>
          </Stack>
          <AutocompleteInput
            name="prompt_suggestions"
            options={[]}
            initialValue={[]}
            maxItemLength={50}
            maxItems={5}
          />
        </StyledFormItem>
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Bubble Text')}
              <ToolTip
                title={t(
                  'The text displayed in the bubble above the chat widget'
                )}
              />
            </Text>
          </Stack>
          <Input
            name="bubble_text"
            required={true}
            maxLength={95}
            withError={!!errors.bubble_text}
            helperText={
              !!errors.bubble_text ? t('Bubble text is required') : ''
            }
            onFocus={() => setActiveField('bubble_text')}
            onBlur={() => setActiveField(undefined)}
          />
        </StyledFormItem>

        {showAccessFields && (
          <ResourceAccessFields
            ownerUserId={ownerUserId}
            resourceLabelKey="chat agent"
          />
        )}
      </Stack>
      <ChatConfigPreview
        formData={formData}
        startMessage={startMessage}
        showPromptSuggestions={(formData.prompt_suggestions?.length ?? 0) > 0}
        activeField={activeField}
      />
    </StyledChatAgentStep>
  );
};

export default ChatAgentSettingsStep;
